import React from 'react';
import { SiteSection, SiteSectionHeader } from '../../components/site';
import type {
  SolutionConnectedGuestJourney,
  SolutionGuestJourneyStage,
  SolutionGuestJourneyStagePosition,
} from './types';

const STAGE_ORDER: readonly SolutionGuestJourneyStagePosition[] = [
  'top',
  'left',
  'right',
  'bottom-left',
  'bottom-right',
];

const HUB = { x: 500, y: 385 };
const NODE_COORDS: Record<SolutionGuestJourneyStagePosition, { x: number; y: number }> = {
  top: { x: 500, y: 108 },
  left: { x: 168, y: 385 },
  right: { x: 832, y: 385 },
  'bottom-left': { x: 268, y: 668 },
  'bottom-right': { x: 732, y: 668 },
};

function JourneyDecor() {
  return (
    <div className="hosp-guest-journey__decor" aria-hidden>
      <svg className="hosp-guest-journey__decor-icon hosp-guest-journey__decor-icon--wifi" viewBox="0 0 24 24" fill="none">
        <path d="M2.5 8.5a14 14 0 0 1 19 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M5.5 11.5a10 10 0 0 1 13 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="12" cy="18" r="1.25" fill="currentColor" />
      </svg>
      <svg className="hosp-guest-journey__decor-icon hosp-guest-journey__decor-icon--cloud" viewBox="0 0 24 24" fill="none">
        <path
          d="M7 18h10a4 4 0 0 0 .4-8 5.5 5.5 0 0 0-10.6 1.8A3.5 3.5 0 0 0 7 18Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>
      <svg className="hosp-guest-journey__decor-icon hosp-guest-journey__decor-icon--lock" viewBox="0 0 24 24" fill="none">
        <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.5" />
        <path d="M8 11V8a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
      <svg className="hosp-guest-journey__decor-icon hosp-guest-journey__decor-icon--graph" viewBox="0 0 24 24" fill="none">
        <path d="M4 18V6M4 18h16M8 14l3-4 3 2 4-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <svg className="hosp-guest-journey__decor-icon hosp-guest-journey__decor-icon--sensor" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" />
        <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M5.6 18.4l1.4-1.4M17 7l1.4-1.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
      <span className="hosp-guest-journey__decor-nodes">
        <span />
        <span />
        <span />
        <span />
        <span />
      </span>
    </div>
  );
}

function ConnectionLines({ stages }: { stages: readonly SolutionGuestJourneyStage[] }) {
  return (
    <svg
      className="hosp-guest-journey__lines"
      viewBox="0 0 1000 750"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden
    >
      <defs>
        <linearGradient id="hosp-gj-line-grad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#2563EB" stopOpacity="0.15" />
          <stop offset="50%" stopColor="#4FD1FF" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0.15" />
        </linearGradient>
        <filter id="hosp-gj-glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <circle
        className="hosp-guest-journey__line-pulse hosp-guest-journey__line-pulse--hub"
        cx={HUB.x}
        cy={HUB.y}
        r="4"
        fill="#2563EB"
        opacity="0.5"
      />

      {stages.map((stage, index) => {
        const end = NODE_COORDS[stage.position];
        const pathId = `hosp-gj-path-${stage.id}`;
        const d = `M ${HUB.x} ${HUB.y} L ${end.x} ${end.y}`;

        return (
          <g key={stage.id}>
            <path
              id={pathId}
              d={d}
              fill="none"
              stroke="url(#hosp-gj-line-grad)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeDasharray="6 8"
              className="hosp-guest-journey__line"
            />
            <circle
              className="hosp-guest-journey__line-pulse"
              cx={end.x}
              cy={end.y}
              r="3.5"
              fill="#4FD1FF"
              opacity="0.65"
              style={{ animationDelay: `${index * 0.35}s` }}
            />
            <circle className="hosp-guest-journey__line-dot" r="2.5" fill="#2563EB" opacity="0.85" filter="url(#hosp-gj-glow)">
              <animateMotion
                dur={`${5.5 + index * 0.5}s`}
                repeatCount="indefinite"
                path={d}
                keyTimes="0;1"
                calcMode="spline"
                keySplines="0.45 0.05 0.55 0.95"
                begin={`${index * 0.6}s`}
              />
            </circle>
            <circle className="hosp-guest-journey__line-dot hosp-guest-journey__line-dot--return" r="2" fill="#4FD1FF" opacity="0.6" filter="url(#hosp-gj-glow)">
              <animateMotion
                dur={`${6 + index * 0.5}s`}
                repeatCount="indefinite"
                path={`M ${end.x} ${end.y} L ${HUB.x} ${HUB.y}`}
                keyTimes="0;1"
                calcMode="spline"
                keySplines="0.45 0.05 0.55 0.95"
                begin={`${2.8 + index * 0.6}s`}
              />
            </circle>
          </g>
        );
      })}

      <circle
        className="hosp-guest-journey__hub-ring"
        cx={HUB.x}
        cy={HUB.y}
        r="160"
        fill="none"
        stroke="url(#hosp-gj-line-grad)"
        strokeWidth="1.5"
        strokeDasharray="4 6"
        opacity="0.45"
      />
    </svg>
  );
}

function JourneyNode({
  stage,
  index,
}: {
  stage: SolutionGuestJourneyStage;
  index: number;
}) {
  return (
    <article
      className={`hosp-guest-journey__node hosp-guest-journey__node--${stage.position} hosp-guest-journey__node--${index + 1}`}
      style={{ '--node-i': index } as React.CSSProperties}
    >
      <div className="hosp-guest-journey__node-visual">
        <div className="hosp-guest-journey__node-ring">
          <span className="hosp-guest-journey__node-emoji" aria-hidden>
            {stage.emoji}
          </span>
          <h3 className="hosp-guest-journey__node-title">{stage.title}</h3>
        </div>
      </div>
      <div className="hosp-guest-journey__node-detail">
        <ul className="hosp-guest-journey__node-list">
          {stage.bullets.map((bullet) => (
            <li key={bullet}>{bullet}</li>
          ))}
        </ul>
        <span className="hosp-guest-journey__node-metric">{stage.metric}</span>
      </div>
    </article>
  );
}

export const SolutionHospitalityGuestJourney: React.FC<{
  journey: SolutionConnectedGuestJourney;
}> = ({ journey }) => {
  const orderedStages = STAGE_ORDER.map((position) =>
    journey.stages.find((stage) => stage.position === position),
  ).filter((stage): stage is SolutionGuestJourneyStage => Boolean(stage));

  return (
    <SiteSection id="experience-journey" className="hosp-guest-journey">
      <JourneyDecor />
      <SiteSectionHeader
        eyebrow={journey.eyebrow}
        title={journey.title}
        subtitle={journey.subtitle}
        align="center"
      />

      <div className="hosp-guest-journey__orbit" aria-label="Connected guest experience journey">
        <ConnectionLines stages={orderedStages} />

        <div className="hosp-guest-journey__hub">
          <div className="hosp-guest-journey__hub-glow" aria-hidden />
          <div className="hosp-guest-journey__hub-inner">
            <div className="hosp-guest-journey__hub-copy">
              <h3 className="hosp-guest-journey__hub-title">{journey.hub.title}</h3>
              <p className="hosp-guest-journey__hub-desc">{journey.hub.description}</p>
            </div>
            <ul className="hosp-guest-journey__hub-caps">
              {journey.hub.capabilities.map((cap) => (
                <li key={cap.label}>
                  <span aria-hidden>{cap.emoji}</span>
                  {cap.label}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {orderedStages.map((stage, index) => (
          <JourneyNode key={stage.id} stage={stage} index={index} />
        ))}
      </div>

      <ol className="hosp-guest-journey__mobile-flow" aria-label="Guest journey stages">
        {orderedStages.slice(0, 2).map((stage, index) => (
          <li key={stage.id} className={`hosp-guest-journey__mobile-step hosp-guest-journey__mobile-step--${index + 1}`}>
            {index > 0 ? <span className="hosp-guest-journey__mobile-connector" aria-hidden /> : null}
            <JourneyNode stage={stage} index={index} />
          </li>
        ))}
        <li className="hosp-guest-journey__mobile-step hosp-guest-journey__mobile-step--hub">
          <span className="hosp-guest-journey__mobile-connector" aria-hidden />
          <div className="hosp-guest-journey__hub hosp-guest-journey__hub--mobile">
            <div className="hosp-guest-journey__hub-glow" aria-hidden />
            <div className="hosp-guest-journey__hub-inner">
              <div className="hosp-guest-journey__hub-copy">
                <h3 className="hosp-guest-journey__hub-title">{journey.hub.title}</h3>
                <p className="hosp-guest-journey__hub-desc">{journey.hub.description}</p>
              </div>
              <ul className="hosp-guest-journey__hub-caps">
                {journey.hub.capabilities.map((cap) => (
                  <li key={cap.label}>
                    <span aria-hidden>{cap.emoji}</span>
                    {cap.label}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </li>
        {orderedStages.slice(2).map((stage, index) => (
          <li
            key={stage.id}
            className={`hosp-guest-journey__mobile-step hosp-guest-journey__mobile-step--${index + 3}`}
          >
            <span className="hosp-guest-journey__mobile-connector" aria-hidden />
            <JourneyNode stage={stage} index={index + 2} />
          </li>
        ))}
      </ol>
    </SiteSection>
  );
};
